import type { LayoutPerson } from './useFamilyTreeLayout';

// ─── Nur zeichnen, was im Bild ist ───────────────────────────────────────────
//
// Bei 3000 Personen stehen 3000 Karten im Dokument, von denen bei lesbarer
// Vergrößerung höchstens ein paar Dutzend zu sehen sind. Der Rest kostet
// Speicher, Abgleicharbeit bei jedem Render und Zeichenzeit – ohne dass ihn
// jemand sieht. Genau deshalb ist die Erkundungsansicht flüssig: Sie zeigt von
// vornherein weniger.
//
// Der Filter darf allerdings nicht bei jedem Schieben neu greifen, sonst
// kostet das Nachrechnen mehr, als das Weglassen spart. Deshalb wird nicht der
// sichtbare Ausschnitt gezeichnet, sondern ein größerer Bereich darum herum –
// und erst wenn der Blick diesen Bereich verlässt, wird neu bestimmt.

export interface Rechteck {
  links: number;
  oben: number;
  rechts: number;
  unten: number;
}

export interface Blickfeld {
  /** Verschiebung und Vergrößerung, wie d3.zoom sie liefert. */
  x: number;
  y: number;
  k: number;
}

export interface Fenstergroesse {
  width: number;
  height: number;
}

/**
 * Wie viel über den sichtbaren Rand hinaus mitgezeichnet wird – als Vielfaches
 * der Fenstergröße je Seite.
 *
 * Eine ganze Fensterbreite Reserve heißt: Man kann das Bild um eine volle
 * Fensterbreite schieben, bevor überhaupt etwas nachgerechnet werden muss.
 * Kleiner wäre sparsamer, ließe aber beim Ziehen leere Ränder aufblitzen.
 */
export const RESERVE = 1;

/**
 * Glättet die negative Null.
 *
 * `-0 / 1` ergibt in JavaScript `-0`, und `Object.is(-0, 0)` ist `false`. Das
 * würde jeden Vergleich auf Gleichheit der Rechtecke unnötig scheitern lassen.
 * Die Addition von 0 macht daraus wieder eine gewöhnliche Null.
 */
function ohneMinusNull(n: number): number {
  return n + 0;
}

/** Der Ausschnitt, den der Nutzer gerade sieht – in Baumkoordinaten. */
export function sichtbarerAusschnitt(blick: Blickfeld, fenster: Fenstergroesse): Rechteck {
  // `|| 1`: Beim allerersten Render steht die Vergrößerung noch auf 0. Ohne
  // diesen Rückfall käme hier Unendlich heraus und der Filter ließe nichts übrig.
  const k = blick.k || 1;
  return {
    links: ohneMinusNull(-blick.x / k),
    oben: ohneMinusNull(-blick.y / k),
    rechts: ohneMinusNull((fenster.width - blick.x) / k),
    unten: ohneMinusNull((fenster.height - blick.y) / k),
  };
}

/** Derselbe Ausschnitt, großzügig erweitert – das ist der Bereich, der gezeichnet wird. */
export function mitReserve(ausschnitt: Rechteck): Rechteck {
  const breite = ausschnitt.rechts - ausschnitt.links;
  const hoehe = ausschnitt.unten - ausschnitt.oben;
  return {
    links: ausschnitt.links - breite * RESERVE,
    oben: ausschnitt.oben - hoehe * RESERVE,
    rechts: ausschnitt.rechts + breite * RESERVE,
    unten: ausschnitt.unten + hoehe * RESERVE,
  };
}

/** Liegt `innen` vollständig in `aussen`? */
export function liegtDrin(innen: Rechteck, aussen: Rechteck): boolean {
  return (
    innen.links >= aussen.links &&
    innen.oben >= aussen.oben &&
    innen.rechts <= aussen.rechts &&
    innen.unten <= aussen.unten
  );
}

/**
 * Muss der gezeichnete Bereich neu bestimmt werden?
 *
 * Nur, wenn der Blick den bisher gezeichneten Bereich verlässt. Solange er
 * darin bleibt, ändert sich nichts – und React bekommt beim Schieben dieselbe
 * Liste wie zuvor, muss also nichts abgleichen.
 */
export function brauchtNeuenBereich(
  sicht: Rechteck,
  gezeichnet: Rechteck | null,
): boolean {
  return gezeichnet === null || !liegtDrin(sicht, gezeichnet);
}

/**
 * Die Karten, die in den gezeichneten Bereich fallen.
 *
 * `halbeBreite`/`halbeHoehe`: Die Position einer Karte ist ihr Mittelpunkt –
 * eine Karte, deren Mittelpunkt knapp außerhalb liegt, ragt trotzdem herein.
 */
export function kartenImBereich(
  personen: LayoutPerson[],
  bereich: Rechteck,
  halbeBreite: number,
  halbeHoehe: number,
): LayoutPerson[] {
  return personen.filter(
    (p) =>
      p.x + halbeBreite >= bereich.links &&
      p.x - halbeBreite <= bereich.rechts &&
      p.y + halbeHoehe >= bereich.oben &&
      p.y - halbeHoehe <= bereich.unten,
  );
}

/**
 * Die zu zeichnenden Karten – mit einer Zusicherung: nie nichts.
 *
 * Der Filter stützt sich auf Verschiebung und Vergrößerung des Blicks. Stimmen
 * die nicht mit dem überein, wo der Baum tatsächlich liegt – etwa weil die
 * Ansicht noch nicht eingepasst wurde –, fällt rechnerisch jede Karte heraus,
 * und der Nutzer sähe eine leere Fläche.
 *
 * Gemessen: Genau das passierte beim ersten Laden. Die Verschiebung stand noch
 * auf dem Startwert (0/0, Faktor 1), der Baum begann aber erst bei y≈960 und
 * reichte in x bis über vier Millionen – die Schnittmenge war leer.
 *
 * Deshalb hier die harte Regel: Kommt nichts heraus, obwohl es Personen gibt,
 * wird alles gezeichnet. Lieber einmal zu viel Arbeit als ein leerer Baum.
 */
export function zuZeichnendeKarten(
  personen: LayoutPerson[],
  bereich: Rechteck | null,
  halbeBreite: number,
  halbeHoehe: number,
): LayoutPerson[] {
  if (bereich === null) return personen;
  const gefiltert = kartenImBereich(personen, bereich, halbeBreite, halbeHoehe);
  return gefiltert.length === 0 ? personen : gefiltert;
}

// ─── Linien und Paarsymbole ──────────────────────────────────────────────────
//
// Beim ersten Anlauf wurden nur die Personenkarten gefiltert. Das reichte
// nicht: Die Verbindungslinien und die Ringe zwischen Partnern blieben
// vollständig im Dokument – bei 3000 Personen über zwölftausend Pfade, die
// auch dann noch gezeichnet wurden, wenn nur eine Handvoll Karten zu sehen
// war. Gemeldet wurde entsprechend, dass es „auch beim Hineinzoomen" langsam
// bleibt.

/** Das umschließende Rechteck einer Familienlinie. */
export function linienRechteck(linie: {
  symbolX: number;
  parentY: number;
  junctionY: number;
  childPositions: Array<{ x: number; y: number }>;
}): Rechteck {
  const xs = [linie.symbolX, ...linie.childPositions.map((c) => c.x)];
  const ys = [linie.parentY, linie.junctionY, ...linie.childPositions.map((c) => c.y)];
  return {
    links: Math.min(...xs),
    oben: Math.min(...ys),
    rechts: Math.max(...xs),
    unten: Math.max(...ys),
  };
}

/** Überschneiden sich zwei Rechtecke? */
export function ueberschneidetSich(a: Rechteck, b: Rechteck): boolean {
  return a.links <= b.rechts && a.rechts >= b.links && a.oben <= b.unten && a.unten >= b.oben;
}

/**
 * Die zu zeichnenden Linien.
 *
 * Eine Linie zählt, sobald ihr umschließendes Rechteck den Bereich berührt –
 * auch wenn nur ihr Querbalken hindurchläuft und beide Enden weit draußen
 * liegen. Genau die verbindet nämlich das, was man gerade sieht, mit dem Rest.
 */
export function zuZeichnendeLinien<
  T extends {
    symbolX: number;
    parentY: number;
    junctionY: number;
    childPositions: Array<{ x: number; y: number }>;
  },
>(linien: T[], bereich: Rechteck | null): T[] {
  if (bereich === null) return linien;
  return linien.filter((l) => ueberschneidetSich(linienRechteck(l), bereich));
}

/** Die zu zeichnenden Paarsymbole – dieselbe Regel wie bei den Karten. */
export function zuZeichnendeSymbole<T extends { x: number; y: number }>(
  symbole: T[],
  bereich: Rechteck | null,
  halbeBreite: number,
  halbeHoehe: number,
): T[] {
  if (bereich === null) return symbole;
  return symbole.filter(
    (s) =>
      s.x + halbeBreite >= bereich.links &&
      s.x - halbeBreite <= bereich.rechts &&
      s.y + halbeHoehe >= bereich.oben &&
      s.y - halbeHoehe <= bereich.unten,
  );
}
